import { datadogLogs } from '@datadog/browser-logs';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useMemo, useState } from 'react';

import { dataLayerPush, segmentIdentify } from '@/hooks/analytics/useTracking';
import { SignUpErrorResponse, SignUpRequest, SignUpResponse } from '@/models/Auth';
import { ErrorHandler, FlattenErrorFieldNamesToErrorList, FlattenErrorFieldNamesToFirstError } from '@/models/Error';
import { monolithApi } from '@/utils/api';
import {
  getStandardErrorMessage,
  hasComplexErrorForField,
  parseAllFieldErrors,
  parseFirstFieldErrors,
} from '@/utils/errorResponseParser';

type UseSignUpInstance = UseMutationResult<
  AxiosResponse<SignUpResponse>,
  AxiosError<SignUpErrorResponse>,
  SignUpRequest
> & {
  fieldErrors: FlattenErrorFieldNamesToFirstError<SignUpErrorResponse>;
  fieldErrorsAll: FlattenErrorFieldNamesToErrorList<SignUpErrorResponse>;
};

export const getCustomErrorMessage: ErrorHandler<SignUpErrorResponse> = (error) => {
  const errorData = error.response?.data;

  if (errorData && hasComplexErrorForField(errorData, 'invitationToken')) {
    return 'Invitation token is invalid. Please contact the organization owner to re-send the invitation e-mail.';
  }

  if (errorData && (hasComplexErrorForField(errorData, 'username') || hasComplexErrorForField(errorData, 'email'))) {
    return null;
  }

  return getStandardErrorMessage(error);
};

export default (): UseSignUpInstance => {
  const [fieldErrorsAll, setFieldErrorsAll] = useState<FlattenErrorFieldNamesToErrorList<SignUpErrorResponse>>({});

  const mutation = useMutation<AxiosResponse<SignUpResponse>, AxiosError<SignUpErrorResponse>, SignUpRequest>({
    mutationFn: async (data) => {
      let fingerprintHash: string | undefined;

      try {
        const { seon } = window;
        if (seon) {
          const config = {
            geolocation: {
              canPrompt: false,
            },
            networkTimeoutMs: 2000,
            fieldTimeoutMs: 2000,
            region: 'eu',
            silentMode: true,
          };
          fingerprintHash = await seon.getSession(config);
        }
      } catch (error) {
        datadogLogs.logger.error(`[SEON fingerprint generation error] ${error}`);
      }

      const reqData = { ...data, fingerprintHash };
      if (data.user.invitationToken) {
        return monolithApi.put('/users/invitation.json', reqData);
      }
      return monolithApi.post('/users.json', reqData);
    },
    onError: (error) => {
      const allFieldErrors = parseAllFieldErrors(error?.response?.data);
      setFieldErrorsAll(allFieldErrors);
    },
    onSuccess: ({ data: { id: userId, slug: userSlug } }) => {
      setFieldErrorsAll({});
      segmentIdentify(userSlug);
      dataLayerPush('Account Created', { userId, userSlug });
    },
  });

  const fieldErrors = useMemo(
    () => parseFirstFieldErrors(mutation.error?.response?.data),
    [mutation.error?.response?.data],
  );
  return { ...mutation, fieldErrors, fieldErrorsAll };
};
